import { motion } from "motion/react";
import { bool, node, number, oneOf, oneOfType } from "prop-types";
import { forwardRef, memo } from "react";

import styled from "styled-components/macro";

// forwardRef inside the memo, or the ref FnrTappyShrink forwards down dies
// here — styled(Container) hands it over and a plain function has nowhere to
// put it. CountryField's flag button is the one caller that passes one.
const Container = memo(
  forwardRef(({ elementType, children, ...rest }, ref) => {
    let Component;
    switch (elementType) {
      case "button":
        Component = motion.button;
        break;
      default:
        Component = motion.div;
    }
    return (
      <Component ref={ref} {...rest}>
        {children}
      </Component>
    );
  })
);

Container.propTypes = {
  elementType: oneOf(["div", "button"]),
  children: node,
};

const FnrTappyShrink = forwardRef(
  /**
   *
   * @param {object} param0
   * @param {"div"|"button"} param0.elementType
   * @param {function} param0.onClick
   * @param {boolean} param0.growOnHover - make it...grow on hover
   * @param {boolean} param0.fullWidth
   * @param {boolean} param0.muted - turns off the effect. Renders children on
   * their own, so a ref you pass in still has nowhere to land when muted.
   * @param {boolean} param0.alignLeft
   * @param {number} param0.scale
   * @param {import("react").Ref} ref
   * @returns
   */
  (
    {
      children,
      elementType = "div",
      growOnHover,
      fullWidth,
      muted,
      alignLeft,
      scale = 0.95,
      ...rest
    },
    ref
  ) => {
    if (muted) return children;
    return (
      <SContainer
        tabIndex={elementType === "button" ? undefined : -1}
        $fullWidth={fullWidth}
        $alignLeft={alignLeft}
        elementType={elementType}
        ref={ref}
        data-cy="FnrTappyShrink_Container"
        whileHover={
          growOnHover
            ? {
                scale: typeof growOnHover === "number" ? growOnHover : 1.1,
                transition: {
                  duration: 0.1,
                },
              }
            : undefined
        }
        whileTap={{
          scale,
          transition: {
            duration: 0.1,
          },
        }}
        {...rest}
      >
        {children}
      </SContainer>
    );
  }
);

FnrTappyShrink.propTypes = {
  elementType: oneOf(["div", "button"]),
  growOnHover: oneOfType([bool, number]),
  fullWidth: bool,
  children: node,
  muted: bool,
  alignLeft: bool,
  scale: number,
};

const SContainer = styled(Container)`
  display: inline-flex;
  align-items: center;
  justify-content: ${(p) => (p.$alignLeft ? "left" : "center")};
  cursor: pointer;
  background: none;
  border: none;
  ${({ $fullWidth }) => ($fullWidth ? "width: 100%;" : "")}
`;

export default FnrTappyShrink;
